/**
 * VendorShareModal.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * The share sheet for a vendor link — built rather than borrowed.
 *
 * WHAT WAS WRONG WITH `navigator.share`
 * -------------------------------------
 * The listing card called it directly, which hands the whole moment to the
 * operating system: an unbranded grey sheet listing every app installed, no
 * sight of what the recipient will actually receive, and — on desktop Chrome,
 * where there is no `navigator.share` at all — a silent clipboard write with a
 * tick as the only feedback. Sharing a restaurant is the single most valuable
 * thing a happy customer can do on this platform, and it was the least
 * designed screen in it.
 *
 * THE PREVIEW IS THE POINT
 * ------------------------
 * The panel at the top of this sheet is not decoration. It renders the card
 * the crawler will build — same image, same 1200x630 pad, same title and
 * description — so a vendor can SEE that their brand is what travels, and so
 * anyone reporting "the link has no preview" can tell in one glance whether
 * the card is missing or merely stale in WhatsApp's cache.
 *
 * That preview is only honest if it stays in step with the edge function that
 * produces the real thing. The `ogShareCard` Cloudinary preset used below is
 * the SAME transform string as OG_CARD_TRANSFORM in
 * netlify/edge-functions/og-inject.js. Change one and change the other.
 *
 * The native sheet is still here — as one option among several, for people who
 * want to send to an app we do not list — instead of being the whole feature.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import IosShareIcon from "@mui/icons-material/IosShare";
import DownloadIcon from "@mui/icons-material/Download";
import { getOptimizedUrl } from "../../utils/cloudinaryOptimize";
import { SITE_ORIGIN } from "../../config/market";

const SITE_HOST = SITE_ORIGIN.replace(/^https?:\/\//, "");

/* Brand marks. MUI ships no channel logos and pulling in a whole icon pack for
   four glyphs would be the wrong trade, so these are the official paths on a
   24x24 grid — the same approach the footer already takes for TikTok. */
const WhatsAppMark = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.86 1.21 3.06c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.04 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.38 9.38 0 0 1-1.44-5A9.42 9.42 0 0 1 18.7 5.32a9.36 9.36 0 0 1 2.76 6.66 9.42 9.42 0 0 1-9.42 9.52zM20.05 3.98A11.32 11.32 0 0 0 12.04.66C5.8.66.72 5.73.72 11.97c0 2 .52 3.95 1.52 5.67L.63 23.34l5.85-1.53a11.28 11.28 0 0 0 5.56 1.42h.01c6.24 0 11.31-5.07 11.32-11.31a11.25 11.25 0 0 0-3.32-8z" />
  </svg>
);

const FacebookMark = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
  </svg>
);

const XMark = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.22-6.82-5.96 6.82H1.68l7.73-8.84L1.25 2.25h6.82l4.71 6.23 5.46-6.23zm-1.16 17.52h1.83L7.01 4.13H5.05l12.03 15.64z" />
  </svg>
);

const TelegramMark = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm4.64 6.8-1.55 7.31c-.12.52-.42.65-.86.4l-2.37-1.75-1.14 1.1c-.13.13-.24.24-.49.24l.17-2.48 4.5-4.07c.2-.17-.04-.27-.3-.1l-5.56 3.5-2.4-.75c-.52-.16-.53-.52.11-.77l9.37-3.61c.43-.16.81.1.67.77z" />
  </svg>
);

/**
 * One channel = one entry here. Kept as data rather than repeated JSX so that
 * adding Instagram or Viber later is one line, and so the order the buttons
 * appear in is a decision made in one visible place rather than a scroll
 * through markup. WhatsApp is first because in this market it is not one
 * channel among many, it is the channel.
 */
const CHANNELS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    tint: "#25D366",
    Mark: WhatsAppMark,
    href: ({ text, url }) =>
      `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    tint: "#1877F2",
    Mark: FacebookMark,
    /* Facebook takes the URL alone and reads the card from our og: tags —
       any `quote` we passed would be ignored by the current sharer. */
    href: ({ url }) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: "x",
    label: "X",
    tint: "#111111",
    Mark: XMark,
    href: ({ text, url }) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    tint: "#229ED9",
    Mark: TelegramMark,
    href: ({ text, url }) =>
      `https://t.me/share/url?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(text)}`,
  },
  {
    key: "email",
    label: "Email",
    tint: "#5B6B7A",
    Mark: (props) => <MailOutlineIcon {...props} />,
    href: ({ text, url, title }) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
        `${text}\n${url}`
      )}`,
  },
];

const VendorShareModal = ({
  open,
  onClose,
  url,
  title,
  description,
  imageUrl,
  /* "cover" for a photograph (card image, cover dish), "pad" for a mark. The
     crawler gets the same hint from the API (ogImageFit), so the preview and
     the card go through the same Cloudinary transform. */
  imageFit = "pad",
  shareText,
}) => {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef(null);
  const closeRef = useRef(null);
  const copyTimer = useRef(null);

  const text = shareText || `${title}. Browse the menu and order:`;

  /* Escape closes, and the page behind stops scrolling while the sheet is up —
     on a phone, a sheet over a scrolling list is how people lose their place
     in a long list of restaurants. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    /* Focus lands on Close rather than the first channel: a stray Enter on an
       opening dialog should dismiss it, never fire off a share. */
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(
    () => () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    },
    []
  );

  useEffect(() => {
    if (!open) {
      setShowQr(false);
      setCopied(false);
    }
  }, [open]);

  const openChannel = useCallback(
    (channel) => {
      const href = channel.href({ text, url, title });
      /* noopener as well as noreferrer: without it the opened tab keeps a
         handle on this window through `opener`. */
      window.open(href, "_blank", "noopener,noreferrer");
    },
    [text, title, url]
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      copyTimer.current = window.setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      /* Clipboard is blocked on insecure origins and by some permission
         policies. A prompt is ugly, but it still lets someone copy by hand —
         which beats a button that does nothing. */
      window.prompt("Copy this link", url);
    }
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({ title, text, url });
    } catch (err) {
      /* AbortError just means the sheet was dismissed. */
      if (err && err.name !== "AbortError") {
        toast.error("Couldn't open the share sheet.");
      }
    }
  }, [text, title, url]);

  const handleDownloadQr = useCallback(() => {
    const canvas = qrRef.current && qrRef.current.querySelector("canvas");
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Couldn't generate the QR code.");
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${(title || "lusuku")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 50)}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("QR code downloaded.");
    }, "image/png");
  }, [title]);

  if (!open) return null;

  /* The same transform the crawler's card gets, so this preview is the card
     and not an impression of it. */
  const previewImage = imageUrl
    ? getOptimizedUrl(
        imageUrl,
        imageFit === "cover" ? "ogShareCardCover" : "ogShareCard"
      )
    : null;

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Overlay onMouseDown={onClose} role="presentation">
      <Sheet
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${title}`}
      >
        <Header>
          <div>
            <Eyebrow>Share</Eyebrow>
            <SheetTitle>{title}</SheetTitle>
          </div>
          <CloseBtn ref={closeRef} onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </CloseBtn>
        </Header>

        <PreviewLabel>What people will see</PreviewLabel>
        <Preview>
          <PreviewMedia>
            {previewImage ? (
              <img src={previewImage} alt="" loading="lazy" />
            ) : (
              <PreviewFallback>{(title || "L").charAt(0)}</PreviewFallback>
            )}
          </PreviewMedia>
          <PreviewBody>
            <PreviewHost>{SITE_HOST}</PreviewHost>
            <PreviewTitle>{title}</PreviewTitle>
            {description ? (
              <PreviewText>{description}</PreviewText>
            ) : null}
          </PreviewBody>
        </Preview>

        <Channels>
          {CHANNELS.map((channel) => (
            <Channel
              key={channel.key}
              type="button"
              $tint={channel.tint}
              onClick={() => openChannel(channel)}
              aria-label={`Share on ${channel.label}`}
            >
              <ChannelIcon $tint={channel.tint}>
                <channel.Mark width={22} height={22} style={{ fontSize: 22 }} />
              </ChannelIcon>
              <span>{channel.label}</span>
            </Channel>
          ))}
          <Channel
            type="button"
            $tint="#1f6f4a"
            onClick={() => setShowQr((v) => !v)}
            aria-expanded={showQr}
            aria-label="Show QR code"
          >
            <ChannelIcon $tint="#1f6f4a">
              <QrCode2Icon style={{ fontSize: 22 }} />
            </ChannelIcon>
            <span>QR code</span>
          </Channel>
        </Channels>

        {showQr ? (
          <QrPanel>
            <QrFrame ref={qrRef}>
              <QRCodeCanvas value={url} size={168} includeMargin level="M" />
            </QrFrame>
            <QrSide>
              <QrHint>
                Print it on a menu, a poster or a receipt. It opens this exact
                page.
              </QrHint>
              <GhostBtn type="button" onClick={handleDownloadQr}>
                <DownloadIcon style={{ fontSize: 18 }} />
                <span>Download PNG</span>
              </GhostBtn>
            </QrSide>
          </QrPanel>
        ) : null}

        <LinkRow>
          <LinkText title={url}>{url}</LinkText>
          <CopyBtn type="button" onClick={handleCopy} $copied={copied}>
            {copied ? (
              <CheckIcon style={{ fontSize: 18 }} />
            ) : (
              <ContentCopyIcon style={{ fontSize: 18 }} />
            )}
            <span>{copied ? "Copied" : "Copy"}</span>
          </CopyBtn>
        </LinkRow>

        {canNativeShare ? (
          <MoreBtn type="button" onClick={handleNativeShare}>
            <IosShareIcon style={{ fontSize: 18 }} />
            <span>More apps</span>
          </MoreBtn>
        ) : null}
      </Sheet>
    </Overlay>
  );
};

export default VendorShareModal;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const INK = "#16252a";
const MUTED = "#61727a";
const LINE = "#e6ebee";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2400;
  background: rgba(10, 20, 24, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  backdrop-filter: blur(2px);

  /* On a phone the sheet belongs at the bottom, where the thumb is. */
  @media (max-width: 560px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 22px;
  padding: 18px 18px 20px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);

  @media (max-width: 560px) {
    max-width: none;
    border-radius: 22px 22px 0 0;
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${MUTED};
`;

const SheetTitle = styled.h3`
  margin: 2px 0 0;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  color: ${INK};
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  border: none;
  background: #f1f4f6;
  color: ${INK};
  cursor: pointer;
  display: flex;
  padding: 7px;
  border-radius: 50%;
  &:hover {
    background: #e4e9ec;
  }
  &:focus-visible {
    outline: 2px solid ${INK};
    outline-offset: 2px;
  }
`;

const PreviewLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-bottom: 7px;
`;

const Preview = styled.div`
  border: 1px solid ${LINE};
  border-radius: 14px;
  overflow: hidden;
  background: #fbfcfd;
`;

/* 1.91:1 is the slot every one of these platforms renders, so the preview is
   pinned to it rather than to whatever the asset happens to be. */
const PreviewMedia = styled.div`
  aspect-ratio: 1.91 / 1;
  background: #ffffff;
  border-bottom: 1px solid ${LINE};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const PreviewFallback = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eef2f4;
  color: ${MUTED};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 800;
`;

const PreviewBody = styled.div`
  padding: 10px 12px 12px;
`;

const PreviewHost = styled.div`
  font-size: 11px;
  color: ${MUTED};
  text-transform: lowercase;
`;

const PreviewTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${INK};
  margin-top: 2px;
`;

const PreviewText = styled.div`
  font-size: 12px;
  color: ${MUTED};
  margin-top: 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Channels = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (min-width: 400px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const Channel = styled.button`
  border: none;
  background: transparent;
  padding: 8px 2px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${INK};

  &:hover {
    background: #f5f8f9;
  }
  &:focus-visible {
    outline: 2px solid ${({ $tint }) => $tint};
    outline-offset: 1px;
  }
`;

const ChannelIcon = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: ${({ $tint }) => $tint};
`;

const QrPanel = styled.div`
  margin-top: 14px;
  padding: 12px;
  border: 1px solid ${LINE};
  border-radius: 14px;
  display: flex;
  gap: 14px;
  align-items: center;
`;

const QrFrame = styled.div`
  flex: 0 0 auto;
  line-height: 0;
  background: #ffffff;
  border-radius: 10px;
`;

const QrSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QrHint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${MUTED};
`;

const GhostBtn = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${LINE};
  background: #ffffff;
  color: ${INK};
  font-size: 12px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: #f5f8f9;
  }
`;

const LinkRow = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${LINE};
  border-radius: 12px;
  padding: 6px 6px 6px 12px;
  background: #fbfcfd;
`;

const LinkText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: ${MUTED};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CopyBtn = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 9px;
  padding: 9px 13px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  color: #ffffff;
  background: ${({ $copied }) => ($copied ? "#1f6f4a" : INK)};
  transition: background 0.15s ease;
`;

const MoreBtn = styled.button`
  margin-top: 10px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid ${LINE};
  background: #ffffff;
  color: ${INK};
  font-size: 13px;
  font-weight: 700;
  padding: 11px;
  border-radius: 12px;
  cursor: pointer;
  &:hover {
    background: #f5f8f9;
  }
`;
